import {
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Flex,
  Checkbox,
  Text,
  HStack,
  Button,
  Tag,
  Badge,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { ActionItem, Rock, Issue, Metric, User } from '../../types';
import { isActionItemOverdue, formatActionItemDate, getActionItemLinkType, getLinkedEntityName } from '../actionItems.util';

interface L10ActionItemListItemProps {
  actionItem: ActionItem;
  users: User[];
  rocks: Rock[];
  issues: Issue[];
  metrics: Metric[];
  onComplete: () => void;
  onEdit: () => void;
  onDrop: () => void;
  onAddToNotes?: () => void;
}

/**
 * L10 Action Item List Item Component
 * Displays a single action item in an accordion format for the L10 Meeting page
 * Uses Chakra UI components with light/dark mode support
 */
export function L10ActionItemListItem({
  actionItem,
  users,
  rocks,
  issues,
  metrics,
  onComplete,
  onEdit,
  onDrop,
  onAddToNotes,
}: L10ActionItemListItemProps) {
  const isOverdue = isActionItemOverdue(actionItem);
  const ownerName = users.find((u) => u.id === actionItem.ownerId)?.name || 'Unassigned';
  const linkType = getActionItemLinkType(actionItem);
  const linkedEntityName = getLinkedEntityName(actionItem, rocks, issues, metrics);

  // Color mode values for dark mode support
  const borderColor = useColorModeValue(
    isOverdue ? 'red.200' : 'gray.200',
    isOverdue ? 'red.700' : 'gray.600'
  );
  const bgColor = useColorModeValue(
    isOverdue ? 'red.50' : 'white',
    isOverdue ? 'red.900' : 'gray.800'
  );
  const panelBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <AccordionItem border="none" mb={2}>
      <Box
        border="1px"
        borderColor={borderColor}
        bg={bgColor}
        borderRadius="md"
        overflow="hidden"
      >
        <h2>
          <AccordionButton>
            <HStack flex="1" spacing={3} align="start">
              <Checkbox
                isChecked={actionItem.complete}
                onChange={onComplete}
                colorScheme="green"
                mt={1}
                onClick={(e) => e.stopPropagation()}
              />
              <Box flex="1" textAlign="left">
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  textDecoration={actionItem.complete ? 'line-through' : 'none'}
                  color={actionItem.complete ? 'gray.500' : undefined}
                >
                  {actionItem.description}
                </Text>
              </Box>
              <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
                - {ownerName}
              </Text>
              {isOverdue && !actionItem.complete && (
                <Badge colorScheme="red" fontSize="xs">
                  OVERDUE
                </Badge>
              )}
            </HStack>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4} bg={panelBg}>
          <VStack align="stretch" spacing={3}>
            <HStack spacing={4} flexWrap="wrap">
              <Text fontSize="sm" color="gray.600">
                Owner: {ownerName}
              </Text>
              <Text fontSize="sm" color="gray.600">
                Due: {formatActionItemDate(actionItem.dueDate)}
              </Text>
              {isOverdue && !actionItem.complete && (
                <Badge colorScheme="red" fontSize="xs">
                  OVERDUE
                </Badge>
              )}
              {linkType !== 'standalone' && linkedEntityName && (
                <Tag size="sm" colorScheme={linkType === 'rock' ? 'purple' : linkType === 'issue' ? 'orange' : 'blue'}>
                  {linkType === 'rock' ? 'Rock' : linkType === 'issue' ? 'Issue' : 'Metric'}: {linkedEntityName}
                </Tag>
              )}
            </HStack>
            <HStack spacing={2}>
              {onAddToNotes && (
                <Button size="sm" variant="outline" onClick={onAddToNotes}>
                  Add to Notes
                </Button>
              )}
              <Button
                size="sm"
                colorScheme="green"
                variant={actionItem.complete ? 'outline' : 'solid'}
                onClick={onComplete}
              >
                {actionItem.complete ? 'Mark Incomplete' : 'Complete'}
              </Button>
              <Button size="sm" variant="outline" onClick={onEdit}>
                Edit
              </Button>
              <Button size="sm" colorScheme="red" variant="outline" onClick={onDrop}>
                Drop
              </Button>
            </HStack>
          </VStack>
        </AccordionPanel>
      </Box>
    </AccordionItem>
  );
}
